import { useState, useCallback, useEffect } from 'react';
import { startOfWeek, endOfWeek, format, eachDayOfInterval } from 'date-fns';
import { supabase } from '../../../../lib/supabaseClient';
import {
  RecurringSchedule,
  OneTimeSchedule,
  TimeSlot,
  StaffAvailabilityRecord,
} from './types';

// Default time slot for new entries
const DEFAULT_SLOT: TimeSlot = {
  startTime: '09:00',
  endTime: '18:00',
};

// Initialize empty recurring schedule (all days disabled)
const createEmptyRecurring = (): RecurringSchedule => {
  const schedule: RecurringSchedule = {};
  for (let i = 0; i < 7; i++) {
    schedule[i] = { isEnabled: false, slots: [] };
  }
  return schedule;
};

// Initialize empty one-time schedule for a week
const createEmptyOneTime = (weekStart: Date): OneTimeSchedule => {
  const days = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { weekStartsOn: 0 }),
  });

  const schedule: OneTimeSchedule = {};
  days.forEach((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    schedule[dateStr] = { isEnabled: false, slots: [] };
  });
  return schedule;
};

// Convert DB time format "HH:mm:ss" to "HH:mm"
const parseTime = (dbTime: string): string => {
  return dbTime.substring(0, 5);
};

// Convert "HH:mm" to "HH:mm:ss" for DB
const formatTimeForDb = (time: string): string => {
  return time + ':00';
};

export function useAvailabilityForm(initialStaffId: string) {
  const [staffId, setStaffId] = useState<string>(initialStaffId);

  // Sync staffId when initialStaffId changes (e.g., when staffMembers loads)
  useEffect(() => {
    if (initialStaffId && !staffId) {
      setStaffId(initialStaffId);
    }
  }, [initialStaffId, staffId]);
  const [recurring, setRecurring] = useState<RecurringSchedule>(createEmptyRecurring());
  const [oneTime, setOneTime] = useState<OneTimeSchedule>({});
  const [selectedWeek, setSelectedWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load recurring schedule from DB
  const loadRecurring = useCallback(async () => {
    if (!staffId) return;

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('staff_availability')
        .select('*')
        .eq('staff_id', staffId)
        .eq('is_recurring', true)
        .order('day_of_week')
        .order('start_time');

      if (dbError) throw dbError;

      // Initialize empty schedule
      const schedule = createEmptyRecurring();

      // Populate from DB records
      if (data) {
        data.forEach((record: StaffAvailabilityRecord) => {
          if (record.day_of_week !== null) {
            const dayOfWeek = record.day_of_week;
            if (!schedule[dayOfWeek]) {
              schedule[dayOfWeek] = { isEnabled: true, slots: [] };
            }
            schedule[dayOfWeek].isEnabled = true;
            schedule[dayOfWeek].slots.push({
              id: record.id,
              startTime: parseTime(record.start_time),
              endTime: parseTime(record.end_time),
            });
          }
        });
      }

      setRecurring(schedule);
    } catch (err: any) {
      setError(err.message || 'Failed to load recurring schedule');
      console.error('Error loading recurring schedule:', err);
    } finally {
      setIsLoading(false);
    }
  }, [staffId]);

  // Load one-time overrides for a specific week
  const loadOneTimeForWeek = useCallback(async (weekStart: Date) => {
    if (!staffId) return;

    setIsLoading(true);
    setError(null);

    try {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

      const { data, error: dbError } = await supabase
        .from('staff_availability')
        .select('*')
        .eq('staff_id', staffId)
        .eq('is_recurring', false)
        .gte('specific_date', weekStartStr)
        .lte('specific_date', weekEndStr)
        .order('specific_date')
        .order('start_time');

      if (dbError) throw dbError;

      // Initialize schedule with recurring data as fallback
      const schedule = createEmptyOneTime(weekStart);

      // First, copy recurring schedule as baseline
      const days = eachDayOfInterval({
        start: weekStart,
        end: weekEnd,
      });

      days.forEach((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayOfWeek = day.getDay();
        const recurringDay = recurring[dayOfWeek];

        if (recurringDay && recurringDay.isEnabled) {
          schedule[dateStr] = {
            isEnabled: true,
            slots: recurringDay.slots.map((slot) => ({
              startTime: slot.startTime,
              endTime: slot.endTime,
            })),
          };
        }
      });

      // Then override with any one-time entries
      if (data) {
        data.forEach((record: StaffAvailabilityRecord) => {
          if (record.specific_date) {
            const dateStr = record.specific_date;
            if (!schedule[dateStr]) {
              schedule[dateStr] = { isEnabled: true, slots: [] };
            }
            // If this is first record for the date, enable and clear slots
            if (schedule[dateStr].slots.length === 0 || !schedule[dateStr].slots.some(s => s.id === record.id)) {
              if (schedule[dateStr].slots.length === 0) {
                schedule[dateStr].isEnabled = true;
              }
            }
            // Only add if not already present
            if (!schedule[dateStr].slots.some(s => s.id === record.id)) {
              schedule[dateStr].slots.push({
                id: record.id,
                startTime: parseTime(record.start_time),
                endTime: parseTime(record.end_time),
              });
            }
          }
        });

        // If there are one-time records, they override recurring completely
        // Find dates that have one-time records and reset their slots
        const datesWithOneTime = new Set(data.map((r: StaffAvailabilityRecord) => r.specific_date));
        datesWithOneTime.forEach((dateStr) => {
          if (dateStr && schedule[dateStr as string]) {
            const oneTimeRecords = data.filter((r: StaffAvailabilityRecord) => r.specific_date === dateStr);
            schedule[dateStr as string] = {
              isEnabled: oneTimeRecords.length > 0,
              slots: oneTimeRecords.map((r: StaffAvailabilityRecord) => ({
                id: r.id,
                startTime: parseTime(r.start_time),
                endTime: parseTime(r.end_time),
              })),
            };
          }
        });
      }

      setOneTime(schedule);
    } catch (err: any) {
      setError(err.message || 'Failed to load one-time schedule');
      console.error('Error loading one-time schedule:', err);
    } finally {
      setIsLoading(false);
    }
  }, [staffId, recurring]);

  // Save recurring schedule (replace strategy)
  // Returns { success: boolean, error?: string }
  const saveRecurring = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!staffId) return { success: false, error: 'No staff selected' };

    setIsSaving(true);
    setError(null);

    try {
      // 1. Delete all existing recurring entries for this staff
      const { error: deleteError } = await supabase
        .from('staff_availability')
        .delete()
        .eq('staff_id', staffId)
        .eq('is_recurring', true);

      if (deleteError) throw deleteError;

      // 2. Collect all enabled slots for insert
      const inserts: Partial<StaffAvailabilityRecord>[] = [];

      Object.entries(recurring).forEach(([dayOfWeekStr, day]) => {
        const dayOfWeek = parseInt(dayOfWeekStr);
        if (day.isEnabled && day.slots.length > 0) {
          day.slots.forEach((slot: TimeSlot) => {
            inserts.push({
              staff_id: staffId,
              day_of_week: dayOfWeek,
              start_time: formatTimeForDb(slot.startTime),
              end_time: formatTimeForDb(slot.endTime),
              is_recurring: true,
              specific_date: null,
            });
          });
        }
      });

      // 3. Insert new entries if any
      if (inserts.length > 0) {
        const { error: insertError } = await supabase
          .from('staff_availability')
          .insert(inserts);

        if (insertError) throw insertError;
      }

      return { success: true };
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to save recurring schedule';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, [staffId, recurring]);

  // Save one-time schedule (per-date upsert)
  // Returns { success: boolean, error?: string }
  const saveOneTime = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!staffId) return { success: false, error: 'No staff selected' };

    setIsSaving(true);
    setError(null);

    try {
      // Get the dates in the current view
      const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 0 });
      const weekStartStr = format(selectedWeek, 'yyyy-MM-dd');
      const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

      // Delete all one-time entries for this week
      const { error: deleteError } = await supabase
        .from('staff_availability')
        .delete()
        .eq('staff_id', staffId)
        .eq('is_recurring', false)
        .gte('specific_date', weekStartStr)
        .lte('specific_date', weekEndStr);

      if (deleteError) throw deleteError;

      // Collect all enabled slots for insert
      const inserts: Partial<StaffAvailabilityRecord>[] = [];

      Object.entries(oneTime).forEach(([dateStr, day]) => {
        if (day.isEnabled && day.slots.length > 0) {
          day.slots.forEach((slot) => {
            inserts.push({
              staff_id: staffId,
              specific_date: dateStr,
              start_time: formatTimeForDb(slot.startTime),
              end_time: formatTimeForDb(slot.endTime),
              is_recurring: false,
              day_of_week: null,
            });
          });
        }
      });

      // Insert new entries if any
      if (inserts.length > 0) {
        const { error: insertError } = await supabase
          .from('staff_availability')
          .insert(inserts);

        if (insertError) throw insertError;
      }

      return { success: true };
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to save one-time schedule';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsSaving(false);
    }
  }, [staffId, oneTime, selectedWeek]);

  // Actions for recurring schedule
  const toggleRecurringDay = useCallback((dayOfWeek: number, enabled: boolean) => {
    setRecurring((prev) => ({
      ...prev,
      [dayOfWeek]: {
        isEnabled: enabled,
        slots: enabled
          ? (prev[dayOfWeek].slots.length === 0 ? [{ ...DEFAULT_SLOT }] : prev[dayOfWeek].slots)
          : [], // Clear slots when disabling to avoid stale data
      },
    }));
  }, []);

  const addRecurringSlot = useCallback((dayOfWeek: number) => {
    setRecurring((prev) => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        slots: [...prev[dayOfWeek].slots, { ...DEFAULT_SLOT }],
      },
    }));
  }, []);

  const removeRecurringSlot = useCallback((dayOfWeek: number, slotIndex: number) => {
    setRecurring((prev) => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        slots: prev[dayOfWeek].slots.filter((_, i) => i !== slotIndex),
      },
    }));
  }, []);

  const updateRecurringSlot = useCallback(
    (dayOfWeek: number, slotIndex: number, field: 'startTime' | 'endTime', value: string) => {
      setRecurring((prev) => ({
        ...prev,
        [dayOfWeek]: {
          ...prev[dayOfWeek],
          slots: prev[dayOfWeek].slots.map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot
          ),
        },
      }));
    },
    []
  );

  // Actions for one-time schedule
  const toggleOneTimeDay = useCallback((dateStr: string, enabled: boolean) => {
    setOneTime((prev) => ({
      ...prev,
      [dateStr]: {
        isEnabled: enabled,
        slots: enabled
          ? ((!prev[dateStr] || prev[dateStr].slots.length === 0) ? [{ ...DEFAULT_SLOT }] : prev[dateStr].slots)
          : [], // Clear slots when disabling to avoid stale data
      },
    }));
  }, []);

  const addOneTimeSlot = useCallback((dateStr: string) => {
    setOneTime((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        slots: [...(prev[dateStr]?.slots || []), { ...DEFAULT_SLOT }],
      },
    }));
  }, []);

  const removeOneTimeSlot = useCallback((dateStr: string, slotIndex: number) => {
    setOneTime((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        slots: prev[dateStr].slots.filter((_, i) => i !== slotIndex),
      },
    }));
  }, []);

  const updateOneTimeSlot = useCallback(
    (dateStr: string, slotIndex: number, field: 'startTime' | 'endTime', value: string) => {
      setOneTime((prev) => ({
        ...prev,
        [dateStr]: {
          ...prev[dateStr],
          slots: prev[dateStr].slots.map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot
          ),
        },
      }));
    },
    []
  );

  // Reset one-time to weekly schedule
  const resetToWeekly = useCallback(() => {
    const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 0 });
    const days = eachDayOfInterval({
      start: selectedWeek,
      end: weekEnd,
    });

    const schedule: OneTimeSchedule = {};
    days.forEach((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayOfWeek = day.getDay();
      const recurringDay = recurring[dayOfWeek];

      schedule[dateStr] = {
        isEnabled: recurringDay?.isEnabled || false,
        slots: recurringDay?.slots.map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
        })) || [],
      };
    });

    setOneTime(schedule);
  }, [selectedWeek, recurring]);

  // Load data when staff changes
  useEffect(() => {
    if (staffId) {
      loadRecurring();
    }
  }, [staffId, loadRecurring]);

  // Load one-time data when week changes or recurring loads
  useEffect(() => {
    if (staffId) {
      loadOneTimeForWeek(selectedWeek);
    }
  }, [staffId, selectedWeek, loadOneTimeForWeek]);

  // Update staff ID handler
  const changeStaff = useCallback((newStaffId: string) => {
    setStaffId(newStaffId);
  }, []);

  // Change week handler
  const changeWeek = useCallback((newWeek: Date) => {
    setSelectedWeek(startOfWeek(newWeek, { weekStartsOn: 0 }));
  }, []);

  return {
    staffId,
    recurring,
    oneTime,
    selectedWeek,
    isLoading,
    isSaving,
    error,
    actions: {
      changeStaff,
      changeWeek,
      loadRecurring,
      loadOneTimeForWeek,
      saveRecurring,
      saveOneTime,
      toggleRecurringDay,
      addRecurringSlot,
      removeRecurringSlot,
      updateRecurringSlot,
      toggleOneTimeDay,
      addOneTimeSlot,
      removeOneTimeSlot,
      updateOneTimeSlot,
      resetToWeekly,
    },
  };
}
